import { FC, useState, useEffect, useMemo } from "react";
import { filter } from "lodash";
import { FormFieldStates } from "@app/shared/qes-standards/forms/exports";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import cx from "classnames";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";

interface ListBoxProps {
  disabled?: boolean;
  value: string[];
  onChange: (value: string[]) => void;
  required: boolean;
  isValid: boolean;
  testId: string;
}

export const ListBox: FC<React.PropsWithChildren<ListBoxProps>> = (props: ListBoxProps) => {
  const [editMode, setEditMode] = useState(false);
  const [inputText, setInputText] = useState("");
  const [viewOnlyInputText, setViewOnlyInputText] = useState("");
  const [disableAdd, setDisableAdd] = useState(true);
  const [disableRemove, setDisableRemove] = useState(true);
  const [options, setOptions] = useState<string[]>(props.value);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [focused, setFocused] = useState(false);
  const [hasBeenFocused, setHasBeenFocused] = useState(false);
  const [fieldState, setFieldState] = useState(FormFieldStates.None);

  useEffect(() => {
    setOptions(props.value);
  }, [props.value]);

  useEffect(() => {
    setDisableAdd(!inputText);
  }, [inputText]);

  useEffect(() => {
    setDisableRemove(selectedOptions.length === 0);
  }, [selectedOptions]);

  useEffect(() => {
    setViewOnlyInputText(options.join(", "));
    setSelectedOptions([]);
    props.onChange(options);
  }, [options]);

  useEffect(() => {
    if (!props.isValid && (props.required) && (hasBeenFocused || focused)) {
      setFieldState(FormFieldStates.Error);
    } else {
      setFieldState(FormFieldStates.None);
    }
  }, [props.isValid, hasBeenFocused, focused, props.required]);

  const optionsDisplay = useMemo(() => {
    return options.map((x, index) => (<option key={index} value={x}>{x}</option>))
  }, [options]);

  const handleAdd = () => {
    if (inputText.trim().length > 0 && options.indexOf(inputText) === -1)
      setOptions(prevOptions => [...prevOptions, inputText]);

    setInputText("");
  }

  const handleAddKeyUp = (e: React.KeyboardEvent) => {
    if (e.key.toLowerCase() === "enter") {
      handleAdd();
    }
  }

  const handleRemove = () => {
    setOptions(prevOptions => {
      for (let i = 0; i < selectedOptions.length; i++) {
        const index = prevOptions.indexOf(selectedOptions[i]);
        prevOptions.splice(index, 1);
      }
      return [...prevOptions];
    });
  }

  const handleOnBlur = () => {
    if (!hasBeenFocused) {
      setHasBeenFocused(true)
    }

    setFocused(false);
  }

  return (
    <div key={props.testId} onBlur={() => handleOnBlur()}>
      {editMode && !props.disabled ?
        (
        <div className="list-box-edit-view">
            <div className={cx({
              "error": fieldState === FormFieldStates.Error,
            })}>
              <QESTextInput
                testId={`${props.testId}__list-box__input`}
                label=""
                hideLabel={true}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyUp={(e) => handleAddKeyUp(e)}
                placeholder="Enter Value"
              />
              <QESSelect
                testId={`${props.testId}__list-box__options`}
                label=""
                hideDropdownIcon={true}
                hideLabel={true}
                allowEmpty={false}
                multiple={true}
                onChange={(e) =>
                  setSelectedOptions(
                    filter(e.target.options, (x) => { return x.selected })
                      .map(x => x.value))}
                value={selectedOptions}
              >
                {optionsDisplay}
              </QESSelect>
            </div>
            <div className="list-box-select-action-row">
              <QESButton qesTestId={`${props.testId}__list-box__add-button`} qesType="secondary" disabled={disableAdd} onClick={() => handleAdd()}>Add</QESButton>
              <QESButton qesTestId={`${props.testId}__list-box__remove-button`} qesType="secondary-danger" disabled={disableRemove} onClick={() => handleRemove()}>Remove</QESButton>
              <QESButton qesTestId={`${props.testId}__list-box__done-button`} qesType="primary" onClick={() => {setEditMode(false); handleOnBlur();}}>Done</QESButton>
            </div>
          </div>
        ) : (
          <div
            className={cx({
              "error": fieldState === FormFieldStates.Error,
              "list-box-view-only": true,
            })}
          >
            <QESTextInput
              testId={`${props.testId}__list-box__input-view-only`}
              label=""
              hideLabel={true}
              disabled={props.disabled}
              inputAppend={
                !props.disabled ?
                  (<a id={`${props.testId}__list-box__edit-button`} className="mt-1" onClick={() => setEditMode(true)}>
                    <span className="list-box-edit-icon"><QesIcon iconName={QesIcons.PencilV1} description="Edit"/></span>
                  </a>) : null
              }
              value={viewOnlyInputText}
              onChange={(e) => setViewOnlyInputText(e.target.value)}
            />
          </div>
        )
      }
    </div>
  );
};
